import { z } from "zod/v4";
import {
  MarketShippingConfigurationSchema,
  MarketShippingSummarySchema,
  ShippingCapabilitiesSchema,
} from "../../dtos/market-shipping-configuration";
import { MarketGidSchema } from "../../dtos/shopify-shipping";

export const GetShippingConfigurationRequestQueryParamsSchema = z.object({
  first: z.coerce.number().int().min(1).max(50).default(20),
  after: z.string().optional(),
  marketId: MarketGidSchema.optional(),
  profileId: z.string().optional(),
  groupId: z.string().optional(),
  zoneId: z.string().optional(),
});

export type GetShippingConfigurationRequestQueryParamsType = z.infer<
  typeof GetShippingConfigurationRequestQueryParamsSchema
>;

const LegacyShippingPageInfoSchema = z.looseObject({
  hasNextPage: z.boolean(),
  hasPreviousPage: z.boolean().optional(),
  endCursor: z.string().nullable().optional(),
});

const LegacyShippingEdgeSchema = z.looseObject({
  cursor: z.string().optional(),
  node: z.looseObject({ id: z.string() }),
});

const LegacyShippingProfileGroupSchema = z.looseObject({
  locationGroup: z.looseObject({
    id: z.string(),
    locations: z.looseObject({
      edges: z.array(LegacyShippingEdgeSchema),
      pageInfo: LegacyShippingPageInfoSchema,
    }),
  }),
  locationGroupZones: z.looseObject({
    edges: z.array(
      z.looseObject({
        cursor: z.string().optional(),
        node: z.looseObject({
          zone: z.looseObject({ id: z.string(), name: z.string() }),
          methodDefinitions: z.looseObject({
            edges: z.array(LegacyShippingEdgeSchema),
            pageInfo: LegacyShippingPageInfoSchema,
          }),
        }),
      })
    ),
    pageInfo: LegacyShippingPageInfoSchema,
  }),
});

export const LegacyShippingConfigurationSchema = z.union([
  z.looseObject({
    deliveryProfiles: z.looseObject({
      edges: z.array(LegacyShippingEdgeSchema),
      pageInfo: LegacyShippingPageInfoSchema,
    }),
  }),
  z.looseObject({
    deliveryProfile: z.looseObject({
      id: z.string(),
      name: z.string(),
      profileLocationGroups: z.array(LegacyShippingProfileGroupSchema),
    }),
  }),
]);

export type LegacyShippingConfigurationType = z.infer<
  typeof LegacyShippingConfigurationSchema
>;

const configurationFields = {
  capabilities: ShippingCapabilitiesSchema,
  markets: z.array(MarketShippingSummarySchema),
  market: MarketShippingConfigurationSchema.nullable(),
  pageInfo: z.object({
    hasNextPage: z.boolean(),
    endCursor: z.string().nullable(),
  }),
};

export const GetShippingConfigurationResponseBodySchema = z.discriminatedUnion(
  "shippingModel",
  [
    z.object({
      ...configurationFields,
      shippingModel: z.literal("legacy"),
      legacy: LegacyShippingConfigurationSchema.nullable(),
    }),
    z.object({
      ...configurationFields,
      shippingModel: z.literal("markets"),
      legacy: z.null(),
    }),
  ]
);

export type GetShippingConfigurationResponseBodyType = z.infer<
  typeof GetShippingConfigurationResponseBodySchema
>;
